// All bot copy, triggers and limits in one place. Keep it free of long dashes.

export const BOT_NAME = 'Routy';
export const MASCOT_THEME = 'teal'; // teal | indigo | sunset (see Mascot.jsx)

export const LIMITS = {
    maxNudgesPerSession: 2,
    nudgeDelayMs: 1200,       // pause after a trigger fires, so it doesn't feel jumpy
    bubbleAutoHideMs: 15000,
    snoozeHours: 24,
    homeIdleMs: 20000,
    homeScrollPx: 600,
};

// Highest priority first
export const SEARCH_TRIGGERS = ['noRoutes', 'allWaitlisted', 'tatkalWindow', 'returnedAfterAway', 'dateFlips', 'deepScrollNoBook', 'exitIntent'];
export const HOME_TRIGGERS = ['exitIntent', 'homeScroll', 'homeIdle'];

// Triggers where the user is clearly stuck. "No thanks" on these offers a seat alert instead.
export const PAIN_TRIGGERS = ['noRoutes', 'allWaitlisted', 'tatkalWindow'];

// Stored as trigger_type on the lead row. Must match BOT_TRIGGER_TYPES in contact_lead_handler.py
export const BOT_TRIGGER_TYPE = {
    noRoutes: 'bot_no_routes',
    allWaitlisted: 'bot_all_waitlisted',
    tatkalWindow: 'bot_tatkal_window',
    returnedAfterAway: 'bot_returned_after_away',
    dateFlips: 'bot_date_flips',
    deepScrollNoBook: 'bot_deep_scroll',
    exitIntent: 'bot_exit_intent',
    homeScroll: 'bot_home_scroll',
    homeIdle: 'bot_home_idle',
    manual: 'bot_manual',     // user tapped the mascot without a nudge
};

export const shortPlace = (label) =>
    String(label || '')
        .replace(/‎/g, '')
        .split(' (')[0]
        .replace(/\s*(railway station|airport)\s*$/i, '')
        .trim();

const dest = (s) => shortPlace(s?.dest_name) || 'your destination';

export const NUDGES = {
    noRoutes: (s) => `Couldn't find a good route to ${dest(s)}. Want our travel team to look for options?`,
    allWaitlisted: (s) => `Direct trains to ${dest(s)} look full 😕 Want our team to try for a confirmed seat?`,
    tatkalWindow: () => 'Travelling tomorrow? Tatkal seats go in minutes. Want our team to try for you?',
    returnedAfterAway: () => "Didn't work out? Our travel team can help you book this trip.",
    dateFlips: () => 'Dates flexible? Our team can find you the best day to travel.',
    deepScrollNoBook: () => 'Not finding the right one? A real person can look for you.',
    exitIntent: () => 'Before you go: want our team to find you a confirmed seat?',
    homeScroll: () => 'Festive rush is on. Need confirmed train or flight tickets? I can help.',
    homeIdle: () => 'Hi! Stuck on a waitlist or hunting for flights? I can help.',
};

export const NUDGE_PRIMARY = (trigger) =>
    (HOME_TRIGGERS.includes(trigger) ? "Let's go" : 'Yes, help me');

export const MOOD_FOR_TRIGGER = (trigger) =>
    (['noRoutes', 'allWaitlisted'].includes(trigger) ? 'sad' : 'wave');

export const COPY = {
    homeGreeting: `Hi, I'm ${BOT_NAME} 👋 Planning a trip, or stuck on a waitlist?`,
    homeMenuAfterNudge: "Pick one and I'll take it from there.",
    searchGreeting: (s) => `Hi, I'm ${BOT_NAME} 👋 Need help with ${s ? `${shortPlace(s.src_name)} → ${dest(s)}` : 'this trip'}?`,
    alreadyHave: "We've got your request ✓ Our travel team will reach out soon.",
    askRoute: 'Where are you travelling from and to?',
    confirmTrip: (summary) => `Got it: ${summary}.`,
    askFlex: 'Are your dates flexible?',
    askModes: 'How would you like to travel? Pick all that work for you.',
    askPhoneCallback: (callLine) => `What's your mobile number? ${callLine}`,
    askPhoneAlert: "Share your number and we'll WhatsApp you as soon as a confirmed seat opens up.",
    phoneNote: "We'll send a confirmation on WhatsApp too.",
    alertOffer: 'No worries. Want a WhatsApp alert if a confirmed seat opens up?',
    bye: "No problem. I'm right here if you need me.",
    handoffChat: "Opening the trip planner for you. It'll find routes in seconds.",
    doneCallback: (phone, when) => `Done! 🎉 Our travel team will call you on ${phone} ${when}.`,
    doneAlert: (phone) => `Done! 🎉 We'll WhatsApp you on ${phone} the moment a confirmed seat opens up.`,
    submitError: "Hmm, that didn't go through. Please try again.",
};

export const FLEX_OPTIONS = [
    { value: 'exact', label: 'Exact date', days: 0 },
    { value: 'pm1', label: '±1 day', days: 1 },
    { value: 'pm3', label: '±2-3 days', days: 3 },
];

// Multi-select in the chat. Order here is the order modes are listed in the lead.
export const MODE_OPTIONS = ['Train', 'Flight', 'Bus'];

// Label on the extended FAB (the "Hi, I'm Routy" pill)
export const FAB_COPY = ({ page, leadSubmitted, pain }) => {
    const title = `Hi, I'm ${BOT_NAME} 👋`;
    if (leadSubmitted) return { title, sub: 'Request received ✓' };
    if (page === 'search') return { title, sub: pain ? 'Seats full? I can help' : 'Can I help with this trip?' };
    return { title, sub: 'Need confirmed tickets?' };
};
